<script setup lang="ts">
import { Form } from "@inertiajs/vue3"
import { useTranslation } from "i18next-vue"
import PersistentLayout from "@/layouts/PersistentLayout.vue"
import Auth from "@/layouts/Auth.vue"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import type { PasswordsEdit } from "@/types/serializers"
import { passwords } from "@/routes"

defineOptions({ layout: [PersistentLayout, Auth] })

const props = defineProps<PasswordsEdit>()

const { t } = useTranslation()
const updateRoute = passwords.update({ token: props.token })

const errs = (error: string | string[] | undefined) =>
  error ? [error].flat() : []
</script>

<template>
  <div class="flex flex-col gap-6">
    <Card>
      <CardHeader class="text-center">
        <CardTitle as="h1" class="text-xl" data-testid="page-title">
          {{ t("passwords.reset.title") }}
        </CardTitle>
        <CardDescription>
          {{ t("passwords.reset.description") }}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form
          :action="updateRoute.url"
          :method="updateRoute.method"
          #default="{ errors, processing }"
        >
          <FieldGroup>
            <Field>
              <FieldLabel for="password">
                {{ t("passwords.reset.password") }}
              </FieldLabel>
              <Input
                id="password"
                name="password"
                type="password"
                autocomplete="new-password"
                required
                :aria-invalid="Boolean(errors.password)"
              />
              <FieldError :errors="errs(errors.password)" />
            </Field>

            <Field>
              <FieldLabel for="password_confirmation">
                {{ t("passwords.reset.password_confirmation") }}
              </FieldLabel>
              <Input
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                autocomplete="new-password"
                required
                :aria-invalid="Boolean(errors.passwordConfirmation)"
              />
              <FieldError :errors="errs(errors.passwordConfirmation)" />
            </Field>

            <Field>
              <Button type="submit" :disabled="processing">
                {{ t("passwords.reset.submit") }}
              </Button>
            </Field>
          </FieldGroup>
        </Form>
      </CardContent>
    </Card>
  </div>
</template>
